/**
 * Created by sswaminathan on 10/11/22
 * File: SingleTenantTsgDropDown
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

// import statement lib, local then scss or css

import { Body } from '@panwds/react-ui'
import { PlusCircleIcon } from '@panwds/icons'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import {
  APP_ID_PAN_CHINA,
  APP_ID_PRISMA_ACCESS,
  APP_ID_PRISMA_ACCESS_EDITION,
  APP_ID_PRISMA_ACCESS_EDITION_ONPREM, APP_ID_PRISMA_ACCESS_FOR_CLEAN_PIPE,
  APP_ID_PRISMA_ACCESS_MOBILE_USERS,
  APP_ID_PRISMA_ACCESS_REMOTE_NETWORKS,
  APP_ID_SDWAN,
  CLOUD_APPS,
  LICENSE_CONTEXT_TO_APP_ID,
  TSG_ERRORS
} from '../../../../../common/constants/GlobalConstants'
import AppContext from '../../../../../common/context/AppContext'
import { ARRAY, isNonemptyArray } from '../../../../../common/utils/Utils'
import { ROOT_NODE_ID } from '../../../../../tenancy/constants/TenancyConstants'
import {
  CHINA_REGION, ERROR_TSG_ALREADY_HAS_OTHER_REGION,
  ERROR_TSG_ALREADY_HAS_PRISMA_ACCESS,
  SASE_SELECT_TENANT
} from '../../../../constants/LabelConstants'
import { getMissingDependencies } from '../../../../util/common'
import { ADD_NEW } from '../../../constants/SubscriptionConstant'
import DropDown from '../../../dropdown/DropDown'

const runningServices = [
  APP_ID_PRISMA_ACCESS,
  APP_ID_PRISMA_ACCESS_EDITION,
  APP_ID_PRISMA_ACCESS_EDITION_ONPREM,
  APP_ID_PRISMA_ACCESS_MOBILE_USERS,
  APP_ID_PRISMA_ACCESS_REMOTE_NETWORKS,
  APP_ID_PRISMA_ACCESS_FOR_CLEAN_PIPE]
const SingleTenantTsgDropDown = (props) => {
  const {
    tenants: _tenants,
    licenseContext,
    //onTSGChange: _onTSGChange,
    apps,
    selectedTSGInstances,
    lockedTSG,
    loading,
    licenses,
    setPageError,
    changeTsgHandler,
    cspDisabled,
    setValidationReTrigger
  } = props

  const { setError, clearErrors, formState, watch, trigger, setValue } = useFormContext()
  const [tenants, setTenants] = useState([])
  const { getAppName } = useContext(AppContext)
  const { isSubmitting, isSubmitted, isDirty, dirtyFields, errors } = formState
  const isCloudManaged = watch('platform')

  useEffect(() => {
    cspDisabled && setValidationReTrigger(true)
  }, [cspDisabled, setValidationReTrigger])
  const availableTenants = useMemo(() => {
    const showAllTsgs = apps[LICENSE_CONTEXT_TO_APP_ID[licenseContext]]?.setup?.extra?.show_all_tsgs
    if (isCloudManaged || showAllTsgs) {
      return _tenants.filter(each => each.id !== ROOT_NODE_ID).map(tenant => ({
        ...tenant,
        display_name: `${tenant.display_name} - ${tenant.id}`
      }))
    }
    // for panorama managed, need to filter out none root TSG
    else {
      return _tenants.filter(each => each.parent_id === ROOT_NODE_ID && each.isAbsoluteRoot && !each.children)
    }
  }, [apps, licenseContext, isCloudManaged, _tenants])


  const isPaChina = useMemo(() => {
    const licenseInfo = licenses?.find(({ app_id }) => app_id === APP_ID_PRISMA_ACCESS_EDITION || app_id === APP_ID_PRISMA_ACCESS_EDITION_ONPREM)
    if (!licenseInfo && !licenseInfo?.entitlements?.length) {
      return false
    }
    return licenseInfo?.entitlements?.some(({ app_id }) => app_id === APP_ID_PAN_CHINA)
  }, [licenses])

  const validateInstanceRunning = useCallback(() => {
    const existingInstances = selectedTSGInstances?.filter(instance => licenses.findIndex(({ app_id }) => app_id === instance.app_id) >= 0)
    if (!isSubmitting && (ARRAY.hasArray(existingInstances) || isNonemptyArray(selectedTSGInstances))) {
      if (existingInstances.find(({ app_id }) => runningServices.includes(app_id)) && !lockedTSG) {
        setError('tsg', { type: 'focus', message: ERROR_TSG_ALREADY_HAS_PRISMA_ACCESS }, { shouldFocus: false })
      }
      else if (isPaChina && selectedTSGInstances?.some((each) => each?.platform_region !== CHINA_REGION || each?.region !== CHINA_REGION)) {
        setError('tsg', { type: 'focus', message: ERROR_TSG_ALREADY_HAS_OTHER_REGION }, { shouldFocus: false })
      }
    }
  }, [isSubmitting, licenses, isPaChina, lockedTSG, selectedTSGInstances, setError])

  const validatePAChinaSkuExist = useCallback(() => {
    const filterPALicense = selectedTSGInstances?.find(each => each.app_id === APP_ID_PRISMA_ACCESS_EDITION || each.app_id === APP_ID_PRISMA_ACCESS_EDITION_ONPREM)
    if (!filterPALicense) {
      return false
    }
    return filterPALicense?.entitlements.find(each => each.app_id === APP_ID_PAN_CHINA)
  }, [selectedTSGInstances])
  useEffect(() => {
    clearErrors(['tsg'])
    if (validatePAChinaSkuExist()) {
      setError('tsg', { type: 'focus', message: TSG_ERRORS[APP_ID_PAN_CHINA] }, { shouldFocus: true })
    }
    // APPORTAL-8141 prevent SDWAN creation in TSG where PA already associated with a outside TSG SDWAN
    if (licenseContext === APP_ID_SDWAN) {
      const paInstance = selectedTSGInstances.find(each => each.app_id === APP_ID_PRISMA_ACCESS)
      if (paInstance && paInstance?._internal?.consumers?.some(each => each.app_id === APP_ID_SDWAN)) {
        setError('tsg', { type: 'focus', message: 'Selected Tenant has a PA instance running which associated with a SDWAN already' }, { shouldFocus: true })
      }
    }
    if (CLOUD_APPS.has(licenseContext)) {
      if (!isSubmitting && isDirty && dirtyFields.tsg && ARRAY.hasArray(selectedTSGInstances) && selectedTSGInstances.has(LICENSE_CONTEXT_TO_APP_ID[licenseContext])) {
        setError('tsg', { type: 'focus', message: TSG_ERRORS[licenseContext] }, { shouldFocus: true })
      }
    }
    else {
      validateInstanceRunning()
    }
    if (!isSubmitting && isDirty && dirtyFields.tsg && !errors['tsg'] && !loading) {
      trigger(['tsg', 'is_eula_accepted'])
    }
  }, [clearErrors, isSubmitting, isSubmitted, isDirty, dirtyFields,
    licenseContext, licenses, selectedTSGInstances, setError, lockedTSG, apps, getAppName, loading, errors, trigger, validateInstanceRunning, validatePAChinaSkuExist])

  const onTSGChange = useCallback((event) => {
    setPageError('')
    clearErrors(['tsg'])
    setValue('support_account', '')
    const tsg = event.target.value
    changeTsgHandler(tsg.id)
  }, [changeTsgHandler, clearErrors, setPageError, setValue])

  useEffect(() => {
    const tenants = []
    try {
      if (availableTenants && availableTenants?.length) {
        tenants.push(...availableTenants?.filter(tenant => {
          if (licenseContext === 'cgx') {
            return tenant?.isRoot
          }
          return true
        })?.map(tenant => ({
          value: tenant?.display_name,
          id: tenant?.id,
          key: tenant?.id,
          description: tenant?.display_name,
          children: tenant?.display_name
        })))
      }
      if (!lockedTSG) {
        tenants?.unshift({
          description: 'Create New Tenant',
          value: 'Create New Tenant',
          children: 'Create New Tenant',
          desc: '',
          id: ADD_NEW,
          key: ADD_NEW,
          iconLeft: <PlusCircleIcon size='sm' className='cm-text-blue-600' />,
        })
      }
    }
    catch (error) {
      setError(error.message)
    }
    setTenants(tenants)
  }, [availableTenants, lockedTSG, licenseContext, onTSGChange, setError])

  const validateTsg = useCallback(() => {
    //validate the associations and co-existance of instances in TSG
    const isValidateDependencies = apps[licenseContext]?.setup?.extra?.validate_dependencies
    if (isValidateDependencies && !loading) {
      const tsgInfo = getMissingDependencies(apps[licenseContext], selectedTSGInstances)
      if (!isSubmitting && isDirty && dirtyFields.tsg && isNonemptyArray(tsgInfo?.missingAssociations)) {
        const requiredAssociations = tsgInfo?.missingAssociations?.map(app_id => getAppName(app_id))
        return `Required association(s) "${requiredAssociations?.join(', ')}" not activated!`
      }
      if (!isSubmitting && isDirty && dirtyFields.tsg && isNonemptyArray(tsgInfo?.notAllowedInstances)) {
        const notAllowedInstances = tsgInfo?.notAllowedInstances?.map(app_id => getAppName(app_id))
        return `Selected Tenant already has instance(s) "${notAllowedInstances?.join(', ')}"`
      }
      return true
    }
    else if (!loading) {
      validateInstanceRunning()
    }
  }, [apps, dirtyFields.tsg, getAppName, isDirty, isSubmitting, licenseContext, loading, selectedTSGInstances, validateInstanceRunning])

  return (<div>
    <DropDown
      addClassName='cmm-modal-select'
      itemList={tenants}
      customDropDown
      required={true}
      fieldName='tsg'
      label={<Body addClassName="tw-font-lato tw-italic" as="span">{SASE_SELECT_TENANT}</Body>}
      height={null}
      width="100%"
      createDisplay={'Create New Tenant'}
      customValidation={validateTsg}
      errorMessage='Selected Tenant is invalid'
      selectProps={{
        label: SASE_SELECT_TENANT,
        showLabel: false,
        disabled: loading,
        enableSearch: true,
        fullWidth: true
      }}
      changeHandler={onTSGChange}
    />
  </div>)
}

SingleTenantTsgDropDown.propTypes = {}
export default SingleTenantTsgDropDown
